/**
 * Fetch 래퍼 — DI 패턴
 *
 * getToken, onUnauthorized는 외부에서 주입 (shared/auth/ import 금지)
 * shared 레이어: config, workspace-pkg만 import 가능
 */

import type { ApiClientOptions, ApiError, ApiResponse, RequestConfig } from "@/shared/api/types";
import { log } from "@/lib/logger";

export type { ApiClientOptions };

/** Log API failures with enough context to debug. 401 is silenced (handled by proxy gate). */
function logApiFailure(
  level: "warn" | "error",
  method: string,
  url: string,
  status: number,
  message: string,
  code: string | undefined,
  bodyExcerpt?: string,
): void {
  if (status === 401) return;
  log[level](`API ${method} ${status}: ${message}`, {
    url,
    method,
    status,
    code,
    body: bodyExcerpt?.slice(0, 500),
  });
}

/** Extract resource IDs from API path for queryable ctx fields. */
function extractRouteIds(pathname: string): Record<string, string> {
  const ids: Record<string, string> = {};
  const seg = pathname.match(
    /\/(products|brands|flash-sale|orders|carts|categories|users)\/([^/?]+)/,
  );
  if (seg) {
    const keyMap: Record<string, string> = {
      products: "productId",
      brands: "brandId",
      "flash-sale": "slug",
      orders: "orderId",
      carts: "cartId",
      categories: "categoryId",
      users: "userId",
    };
    ids[keyMap[seg[1]] || seg[1]] = seg[2];
  }
  return ids;
}

/** Log every API call (success included) with route metadata. */
function logApiSuccess(method: string, url: string, status: number, durationMs: number): void {
  try {
    const parsed = new URL(url, "http://localhost");
    const path = parsed.pathname;
    log.info(`API ${method} ${status}: ${path}`, {
      method,
      status,
      durationMs: Math.round(durationMs),
      ...extractRouteIds(path),
    });
  } catch {
    // never let logging break the app
  }
}

/** URL에 query params 직렬화. baseUrl의 path prefix(/api/v1 등)를 보존한다. */
function buildUrl(baseUrl: string, path: string, params?: RequestConfig["params"]): string {
  const normalizedBase = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  const raw = `${normalizedBase}${normalizedPath}`;

  // 상대 경로(/api/v1)인 경우 현재 origin을 base로 사용
  const origin = typeof window !== "undefined" ? window.location.origin : "http://localhost:3000";
  const url = new URL(raw, origin);

  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value === undefined) continue;
      if (Array.isArray(value)) {
        for (const item of value) {
          url.searchParams.append(key, String(item));
        }
      } else {
        url.searchParams.set(key, String(value));
      }
    }
  }
  return url.toString();
}

/** fetch 에러를 ApiError로 변환 */
function toApiError(status: number, message: string, code?: string, detail?: string): ApiError {
  return detail === undefined ? { status, message, code } : { status, message, code, detail };
}

export interface ApiClient {
  get<T>(path: string, config?: RequestConfig): Promise<ApiResponse<T>>;
  post<T>(path: string, body?: unknown, config?: RequestConfig): Promise<ApiResponse<T>>;
  put<T>(path: string, body?: unknown, config?: RequestConfig): Promise<ApiResponse<T>>;
  patch<T>(path: string, body?: unknown, config?: RequestConfig): Promise<ApiResponse<T>>;
  del<T>(path: string, body?: unknown, config?: RequestConfig): Promise<ApiResponse<T>>;
}

/**
 * API 클라이언트 팩토리
 *
 * - 토큰은 custom `token` header로 주입 (레거시 백엔드 계약)
 * - 401 응답은 호출자에서 처리 (프록시 미들웨어가 보호 라우트 가드)
 */
export function createApiClient(options: ApiClientOptions): ApiClient {
  const { baseUrl, getToken, onUnauthorized } = options;

  async function request<T>(
    method: string,
    path: string,
    body?: unknown,
    config?: RequestConfig,
  ): Promise<ApiResponse<T>> {
    const t0 = performance.now();
    const url = buildUrl(baseUrl, path, config?.params);

    const isFormData = body instanceof FormData;
    const headers: Record<string, string> = {
      ...(isFormData ? {} : { "Content-Type": "application/json" }),
      ...config?.headers,
    };

    const token = getToken();
    if (token) {
      headers["token"] = `Bearer ${token}`;
    }

    let response: Response;
    try {
      response = await fetch(url, {
        method,
        headers,
        body: body !== undefined ? (isFormData ? body : JSON.stringify(body)) : undefined,
        signal: config?.signal,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Network error";
      // AbortController.abort()는 정상 흐름이라 무음.
      // 브라우저가 navigation으로 네이티브 취소할 땐 TypeError("Failed to fetch" /
      // "Load failed")를 던지는데, 진짜 네트워크 장애와 구분이 어렵다. 정보 유실
      // 대신 warn으로 낮춰서 남기고, 진짜 서버 장애(5xx 응답이 도착한 경우)는
      // 아래 response.ok 분기에서 "error"로 기록된다.
      if (err instanceof Error && err.name === "AbortError") {
        // fully silent
      } else {
        const looksLikeNavigationAbort =
          err instanceof TypeError &&
          /Failed to fetch|Load failed|network connection was lost/i.test(err.message);
        logApiFailure(
          looksLikeNavigationAbort ? "warn" : "error",
          method,
          url,
          0,
          message,
          "NETWORK_ERROR",
        );
      }
      throw toApiError(0, message, "NETWORK_ERROR");
    }

    // 빈 응답 body 처리 (SMS 발송 등 204/200 with empty body)
    const text = await response.text();
    let json: unknown;
    if (text.length === 0) {
      if (!response.ok) {
        logApiFailure(
          response.status >= 500 ? "error" : "warn",
          method,
          url,
          response.status,
          response.statusText,
          undefined,
        );
        throw toApiError(response.status, response.statusText, undefined);
      }
      logApiSuccess(method, url, response.status, performance.now() - t0);
      return { data: null as T, status: response.status, message: "ok" };
    }
    try {
      json = JSON.parse(text);
    } catch {
      if (!response.ok) {
        logApiFailure(
          response.status >= 500 ? "error" : "warn",
          method,
          url,
          response.status,
          text || response.statusText,
          "PARSE_ERROR",
          text,
        );
        throw toApiError(response.status, text || response.statusText, "PARSE_ERROR");
      }
      logApiSuccess(method, url, response.status, performance.now() - t0);
      return { data: text as T, status: response.status, message: "ok" };
    }

    if (!response.ok) {
      const errBody = json as Record<string, unknown>;
      // 백엔드 에러 형태: { message: "..." } · { error: { message: "..." } } · { error: "..." }
      const nested = errBody?.error as Record<string, unknown> | undefined;
      const detail = typeof errBody?.error === "string" ? errBody.error : undefined;
      const message =
        typeof nested?.message === "string"
          ? nested.message
          : typeof errBody?.message === "string"
            ? errBody.message
            : (detail ?? response.statusText);
      const code =
        typeof nested?.code === "string"
          ? nested.code
          : typeof errBody?.code === "string"
            ? errBody.code
            : undefined;
      logApiFailure(
        response.status >= 500 ? "error" : "warn",
        method,
        url,
        response.status,
        message,
        code,
        text,
      );
      if (response.status === 401 && onUnauthorized) {
        const returnUrl =
          typeof window !== "undefined" ? window.location.pathname + window.location.search : path;
        onUnauthorized(returnUrl);
      }
      throw toApiError(response.status, message, code, detail);
    }

    logApiSuccess(method, url, response.status, performance.now() - t0);

    // 백엔드 응답 감지: { data, status|code|success, message } 형태면 통과
    // 레거시: { code, data }, 신규 Hono: { success, data }
    const body_ = json as Record<string, unknown>;
    const hasData = typeof body_?.data !== "undefined";
    const hasEnvelope =
      typeof body_?.status === "number" ||
      typeof body_?.code === "number" ||
      typeof body_?.success === "boolean";
    if (hasData && hasEnvelope) {
      return {
        data: body_.data as T,
        status: response.status,
        message: typeof body_?.message === "string" ? body_.message : "ok",
      };
    }

    return {
      data: json as T,
      status: response.status,
      message: "ok",
    };
  }

  return {
    get<T>(path: string, config?: RequestConfig) {
      return request<T>("GET", path, undefined, config);
    },
    post<T>(path: string, body?: unknown, config?: RequestConfig) {
      return request<T>("POST", path, body, config);
    },
    put<T>(path: string, body?: unknown, config?: RequestConfig) {
      return request<T>("PUT", path, body, config);
    },
    patch<T>(path: string, body?: unknown, config?: RequestConfig) {
      return request<T>("PATCH", path, body, config);
    },
    del<T>(path: string, body?: unknown, config?: RequestConfig) {
      return request<T>("DELETE", path, body, config);
    },
  };
}
